// Popup shown when creating an AMEEX parcel fails.
//
// WHY (2026-07-29, order #D382): a parcel for OUED LAOU was never created, but
// the UI reported success — and when AMEEX did return an error, it surfaced as
// one small line of raw technical text under the form. Nouhaila re-edited the
// order three times in 90 seconds without ever learning what was wrong.
//
// Rules here: the operator sees WHAT failed and WHAT TO DO, in plain French,
// with the guarantee that the parcel was NOT created (so they don't fear a
// duplicate). Raw AMEEX output is available but folded away — it must never be
// the whole message.
import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { useT } from '@/lib/i18n';

export interface AmeexFailureInfo {
  reason: string;
  action?: string;
  detail?: string;
  kind?: string;
}

/** Pulls the structured explanation off an apiFetch error, with a safe fallback
 *  so an unexpected error shape still produces a readable popup. */
export function toAmeexFailure(e: unknown): AmeexFailureInfo {
  const body = (e as { body?: Partial<AmeexFailureInfo> })?.body;
  const message = (e as Error)?.message;
  return {
    reason: body?.reason ?? message ?? 'Erreur inconnue.',
    action: body?.action,
    detail: body?.detail,
    kind: body?.kind,
  };
}

export function AmeexErrorDialog({
  failure,
  onClose,
}: {
  failure: AmeexFailureInfo | null;
  onClose: () => void;
}) {
  const { t } = useT();
  const [showDetail, setShowDetail] = useState(false);

  if (!failure) return null;

  return (
    <Modal isOpen onClose={onClose} title={t('Colis AMEEX non créé')} size="md">
      <div className="px-5 py-5 space-y-4">
        <div className="flex gap-3">
          <span
            className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center text-xl bg-[#FEE2E2] text-status-stuck dark:bg-status-stuck-bg"
            aria-hidden
          >
            !
          </span>
          <div className="min-w-0 space-y-3">
            <p className="text-sm text-gray-900 leading-relaxed">{failure.reason}</p>

            {failure.action && (
              <div
                className="rounded-lg px-3 py-2.5 text-sm leading-relaxed bg-[rgba(201,162,74,0.10)] text-[#6B5423] dark:bg-adm-gold-wash dark:text-adm-gold-ink"
              >
                <span className="font-semibold">{t('À faire :')} </span>
                {failure.action}
              </div>
            )}

            {/* Reassurance: the #1 worry on a failed shipment is a double parcel. */}
            <p className="text-xs text-gray-500">
              {t('Aucun colis n’a été créé. Vous pouvez corriger et réessayer sans risque de doublon.')}
            </p>
          </div>
        </div>

        {failure.detail && (
          <div className="pt-1">
            <button
              type="button"
              onClick={() => setShowDetail((v) => !v)}
              className="text-xs text-gray-500 underline hover:text-gray-700"
            >
              {showDetail ? t('Masquer les détails techniques') : t('Détails techniques')}
            </button>
            {showDetail && (
              <pre className="mt-2 max-h-40 overflow-auto rounded-lg bg-gray-50 p-3 text-[11px] text-gray-600 whitespace-pre-wrap break-all">
                {failure.detail}
              </pre>
            )}
          </div>
        )}

        <div className="flex justify-end pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: '#C9A24A' }}
          >
            {t('Compris')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
